import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { CHANNEL_SHORT_NAME_MAP, IChannel, PROTOCOL } from '@/entities/channel';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Link } from '@/shared/ui/lib/Link';
import { Stack, Typography } from '@/shared/ui';
import { ControlledTextField } from '@/shared/ui/TextField';

import { useWildberriesConnect } from '../../model/wildberries/useWildberriesConnect';
import { wildberriesConnectFormDataSchema } from '../../model/schemas';
import { ChannelIconWithLabel } from '../ChannelIconWithLabel';

const FORM_ID = 'wildberries-connect-form';
const WILDBERRIES_TOKEN_URL = 'https://seller.wildberries.ru/supplier-settings/access-to-api';

interface WildberriesConnectFormProps extends TDialogBase {
  channel: IChannel;
  onAuthorize?: () => void;
}

export const WildberriesConnectForm = ({
  open,
  onClose,
  onAuthorize,
  channel,
}: WildberriesConnectFormProps) => {
  const { t } = useTranslation();
  const { connect, isPending } = useWildberriesConnect();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    resolver: zodResolver(wildberriesConnectFormDataSchema(t)),
    defaultValues: {
      token: '',
      name: channel.name ?? '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = handleSubmit(async (data) => {
    await connect({
      channelId: channel.id,
      token: data.token,
      name: data.name,
    });
    reset();
    onAuthorize?.();
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('channels:wildberries.authTitle')}
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.WILDBERRIES]}
          protocol={PROTOCOL.WILDBERRIES}
        />
      }
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('channels:buttons.closeModal')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isPending}
        >
          {t('channels:wildberries.connect')}
        </Button>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit}>
        <Stack spacing={2}>
          <ControlledTextField
            name="token"
            control={control}
            autoFocus
            type="password"
            label={t('channels:wildberries.token')}
          />
          <Typography variant="body2">
            <Link href={WILDBERRIES_TOKEN_URL} target="_blank" rel="noopener noreferrer">
              {t('channels:wildberries.tokenLink')}
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('channels:wildberries.tokenHint')}
          </Typography>
          <ControlledTextField
            name="name"
            control={control}
            label={t('channels:wildberries.name')}
          />
        </Stack>
      </form>
    </Dialog>
  );
};
